import { useEffect, useState } from 'react';

export type EmailUseCasePreference = 'transactional' | 'marketing' | 'both';

const PREFERENCE_EVENT = 'simplysend:email-use-case-changed';
const STORAGE_PREFIX = 'simplysend:email-use-case:v1:';

function storageKey(userId?: string | null) {
  return userId ? `${STORAGE_PREFIX}${userId}` : null;
}

function isEmailUseCasePreference(value: unknown): value is EmailUseCasePreference {
  return value === 'transactional' || value === 'marketing' || value === 'both';
}

function readPreference(userId?: string | null): EmailUseCasePreference | null {
  const key = storageKey(userId);
  if (!key || typeof window === 'undefined') return null;
  try {
    const value = window.localStorage.getItem(key);
    return isEmailUseCasePreference(value) ? value : null;
  } catch {
    return null;
  }
}

export function useEmailUseCasePreference(userId?: string | null) {
  const [storedPreference, setStoredPreference] = useState<{ userId?: string | null; value: EmailUseCasePreference | null }>(() => ({ userId, value: readPreference(userId) }));
  const preference = storedPreference.userId === userId ? storedPreference.value : readPreference(userId);

  useEffect(() => {
    const key = storageKey(userId);
    if (!key || typeof window === 'undefined') return;

    const handlePreferenceChange = (event: Event) => {
      const detail = (event as CustomEvent<{ userId?: string; preference?: unknown }>).detail;
      if (detail?.userId === userId && isEmailUseCasePreference(detail.preference)) {
        setStoredPreference({ userId, value: detail.preference });
      }
    };
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === key || event.key === null) setStoredPreference({ userId, value: readPreference(userId) });
    };

    window.addEventListener(PREFERENCE_EVENT, handlePreferenceChange);
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener(PREFERENCE_EVENT, handlePreferenceChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [userId]);

  const savePreference = (value: EmailUseCasePreference) => {
    const key = storageKey(userId);
    if (!key || typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(key, value);
    } catch {
      // Keep the choice active for this page session when browser storage is unavailable.
    }
    setStoredPreference({ userId, value });
    window.dispatchEvent(new CustomEvent(PREFERENCE_EVENT, { detail: { userId, preference: value } }));
  };

  return { preference, setPreference: savePreference };
}
